import { clsx } from 'clsx';
import {
  MARK_BOLD,
  MARK_CODE,
  MARK_HIGHLIGHT,
  MARK_LINK,
  MARK_STRIKE,
  MARK_STYLED,
  MARK_SUPERSCRIPT,
  MARK_TEXT_STYLE,
  MARK_UNDERLINE,
  NODE_HEADING,
  NODE_HR,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_UL,
  RenderOptions,
} from 'storyblok-rich-text-react-renderer';

import { Fragment } from 'react';

import { Typography, SharedLink } from '@monoimin/atoms';

import { checkParagraphContainsOnlyFlag } from '@monoimin/utils/helpers';
import { ValidBlok } from '@monoimin/ui-core';

import { newRenderWithMarks } from '@monoimin/atoms/utils';

import { SbRichTextProps } from '../types';

import { levelToVariant } from './levelToVariant';

export const getAllResolvers = ({
  theme,
  blokResolvers,
  classNames,
}: SbRichTextProps) => {
  const listCombinedClassName = clsx(
    classNames?.list,
    classNames?.paragraph || 'body'
  );

  const resolvers: RenderOptions = {
    nodeResolvers: {
      [NODE_PARAGRAPH]: (children) => {
        return (
          <Typography className={classNames?.paragraph} theme={theme}>
            {newRenderWithMarks({ children })}
          </Typography>
        );
      },
      [NODE_HR]: () => {
        return (
          <hr className='border-transparent border-b h-[1px] border-b-line my-9!' />
        );
      },

      [NODE_HEADING]: (children, props) => {
        // h1, h2, h5, h6 рендерим как обычные параграфы
        if (
          props.level === 1 ||
          props.level === 2 ||
          props.level === 5 ||
          props.level === 6
        ) {
          return (
            <Typography className={classNames?.paragraph} theme={theme}>
              {newRenderWithMarks({ children })}
            </Typography>
          );
        }

        // h3, h4 обрабатываем с проверкой флагов
        const containsOnlyFlag = checkParagraphContainsOnlyFlag(
          (children as string[])?.[0]
        );

        if (containsOnlyFlag) {
          return (
            <Typography
              flagSize={props.level === 3 ? 'medium' : 'small'}
              className={clsx(levelToVariant(props.level), 'mb-3.5')}
            >
              {children}
            </Typography>
          );
        }

        return (
          <Typography
            as={levelToVariant(props.level)}
            flagSize={props.level === 3 ? 'medium' : 'small'}
            className={clsx(levelToVariant(props.level), 'mb-3.5')}
          >
            {children}
          </Typography>
        );
      },
      [NODE_OL]: (children) => (
        <ol className={clsx('ordered-list', listCombinedClassName)}>
          {children}
        </ol>
      ),
      [NODE_UL]: (children) => (
        <ul className={clsx('unordered-list', listCombinedClassName)}>
          {children}
        </ul>
      ),

      [NODE_LI]: (children) => (
        <li className={clsx('pl-1', classNames?.li)}>
          {newRenderWithMarks({ children })}
        </li>
      ),
    },

    markResolvers: {
      [MARK_LINK]: (children, props) => {
        if (!props.href) {
          return (
            <Typography className={clsx(classNames?.paragraph)} theme={theme}>
              {children}
            </Typography>
          );
        }
        const additionalparams =
          typeof children === 'string' ? { 'aria-label': children } : {};
        return (
          <SharedLink
            theme={theme}
            linkType={props?.linktype === 'story' ? 'internal' : 'external'}
            {...additionalparams}
            href={props.href}
            underline='hover'
            className={clsx(
              classNames?.paragraph,
              classNames?.link,
              'shared-link-rich-text ghost-link'
            )}
          >
            {newRenderWithMarks({ children })}
          </SharedLink>
        );
      },
      [MARK_BOLD]: (children) => {
        return <strong className={classNames?.bold}>{children}</strong>;
      },
      [MARK_TEXT_STYLE]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
      [MARK_HIGHLIGHT]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
      [MARK_STYLED]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
      [MARK_SUPERSCRIPT]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
      [MARK_STRIKE]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
      [MARK_CODE]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
      [MARK_UNDERLINE]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
    },
    blokResolvers,
    defaultBlokResolver: (name) => (
      <ValidBlok blok={undefined} blokName={name} />
    ),
  };
  return resolvers;
};
